import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import hero from "../assets/hero.jpg";

const WhyNowSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -30, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const benefits = [
    {
      icon: "🚀",
      title: "Early access",
      description: "Soyez parmi les premiers à avoir votre jardin aquaponique intelligent",
    },
    {
      icon: "💰",
      title: "Prix du kit réduit",
      description: "Tarif préférentiel exclusif pour nos bêta-testeurs",
    },
    {
      icon: "🛠️",
      title: "Support premium",
      description: "Accompagnement personnalisé tout au long de votre expérience",
    },
    {
      icon: "🌍",
      title: "Projet éco-responsable",
      description: "Participez à un projet à fort impact environnemental",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-aquagray to-aquablue relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-aquagreen/20 via-transparent to-blue-900/20" />
          <img
            src={hero}
            alt="Système aquaponique"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 backdrop-blur-lg" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          {/* Left Content */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-aquagreen mb-6 leading-tight">
                Participez à notre campagne de test
              </h2>
              <p className="text-xl text-aquacream leading-relaxed">
                Nous recherchons nos 10 premiers testeurs. Vous recevrez un kit
                tout inclus à prix fortement réduit avec un support personnalisé.
                En retour, vous nous partagerez votre expérience et nous
                collecterons les données du jardin pour l'améliorer.
              </p>
            </div>

            <motion.a
              href="https://forms.gle/gv7DaeyYDsvayxjXA"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-8 py-4 bg-aquagreen text-aquablue font-bold text-lg rounded-full hover:bg-aquagreen transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25"
            >
              Rejoindre l'aventure
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.a>
          </motion.div>

          {/* Right Content - Benefits */}
          <motion.div variants={itemVariants} className="space-y-6">
            <h3 className="text-2xl font-bold text-aquagreen mb-8">
              Bénéfices pour les testeurs :
            </h3>
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="text-3xl">{benefit.icon}</div>
                  <div>
                    <h4 className="font-semibold text-aquagreen text-lg mb-1">
                      {benefit.title}
                    </h4>
                    <p className="text-aquacream text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyNowSection;